import React, { useState, useEffect } from 'react'
import { supabase } from '../../supabase'

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', color: 'var(--text)',
  width: '100%', boxSizing: 'border-box', outline: 'none'
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace',
  fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

export default function AdminMessages({ session }) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')
  const [form, setForm] = useState({ title: '', message: '', target_role: 'all', is_active: true })

  useEffect(() => { fetchMessages() }, [])

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3000) }

  async function fetchMessages() {
    const { data } = await supabase
      .from('admin_messages')
      .select('*')
      .order('created_at', { ascending: false })
    if (data) setMessages(data)
    setLoading(false)
  }

  async function saveMessage() {
    if (!form.title.trim() || !form.message.trim()) { showToast('Please fill title and message'); return }
    setSaving(true)
    const { error } = await supabase.from('admin_messages').insert({
      title: form.title.trim(),
      message: form.message.trim(),
      target_role: form.target_role,
      is_active: form.is_active
    })
    if (error) { showToast('❌ Error: ' + error.message) }
    else {
      showToast('✅ Message published!')
      setForm({ title: '', message: '', target_role: 'all', is_active: true })
      setShowForm(false)
      fetchMessages()
    }
    setSaving(false)
  }

  async function toggleMessage(msg) {
    const { error } = await supabase
      .from('admin_messages')
      .update({ is_active: !msg.is_active })
      .eq('id', msg.id)
    if (error) { showToast('❌ Error: ' + error.message) }
    else { showToast(msg.is_active ? '⛔ Message hidden' : '✅ Message activated'); fetchMessages() }
  }

  async function deleteMessage(id) {
    if (!window.confirm('Delete this message?')) return
    const { error } = await supabase.from('admin_messages').delete().eq('id', id)
    if (error) { showToast('❌ Error: ' + error.message) }
    else { showToast('🗑️ Message deleted'); fetchMessages() }
  }

  const targetLabels = { all: '👥 All Users', user: '🙋 Regular Users', gifted: '🎁 Gifted Users', admin: '🔧 Admins Only' }

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', background: '#0d0d0d', color: '#f5f0e8', padding: '0.7rem 1.1rem', borderRadius: '6px', fontSize: '0.8rem', zIndex: 9999, borderLeft: '3px solid #c9a84c' }}>{toast}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.8rem' }}>
        <div>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0, color: 'var(--text)' }}>Admin Messages</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Send announcements and recommendations to users.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{ background: '#0d0d0d', color: '#f5f0e8', border: 'none', padding: '0.55rem 1.1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          + New Message
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: showForm ? '1fr 1fr' : '1fr', gap: '1.5rem' }}>

        {/* Messages List */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>All Messages ({messages.length})</span>
          </div>

          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : messages.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📢</div>
              <div style={{ fontWeight: '500', marginBottom: '0.3rem' }}>No messages yet</div>
              <div style={{ fontSize: '0.8rem' }}>Create announcements to show users on their dashboard.</div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={msg.id} style={{ padding: '1rem 1.4rem', borderBottom: i < messages.length - 1 ? '1px solid #d4cfc5' : 'none', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.8rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '600', fontSize: '0.88rem', color: 'var(--text)' }}>{msg.title}</span>
                      {!msg.is_active && <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', background: 'rgba(184,68,26,0.1)', color: '#b8441a', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>HIDDEN</span>}
                      <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', background: 'rgba(201,168,76,0.12)', color: '#7a5d1e', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>{targetLabels[msg.target_role]}</span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.3rem', lineHeight: 1.5 }}>{msg.message}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                      {new Date(msg.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    <button onClick={() => toggleMessage(msg)} style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.55rem', borderRadius: '3px', fontSize: '0.65rem', fontFamily: 'monospace', cursor: 'pointer', color: msg.is_active ? '#b8441a' : '#4a6741' }}>
                      {msg.is_active ? 'Hide' : 'Show'}
                    </button>
                    <button onClick={() => deleteMessage(msg.id)} style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.55rem', borderRadius: '3px', fontSize: '0.65rem', fontFamily: 'monospace', cursor: 'pointer', color: '#c0392b' }}>Del</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* New Message Form */}
        {showForm && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>New Message</span>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem' }}>✕</button>
            </div>
            <div style={{ padding: '1.2rem 1.4rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={labelStyle}>Title *</label>
                <input style={inputStyle} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. New Feature Alert!" />
              </div>
              <div>
                <label style={labelStyle}>Message *</label>
                <textarea
                  style={{ ...inputStyle, height: '100px', resize: 'vertical', fontFamily: 'sans-serif', lineHeight: 1.5 }}
                  value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="Write your announcement or recommendation here..."
                />
              </div>
              <div>
                <label style={labelStyle}>Target Audience</label>
                <select style={inputStyle} value={form.target_role} onChange={e => setForm(p => ({ ...p, target_role: e.target.value }))}>
                  <option value="all">👥 All Users</option>
                  <option value="user">🙋 Regular Users Only</option>
                  <option value="gifted">🎁 Gifted Premium Users</option>
                  <option value="admin">🔧 Admins Only</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.82rem' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                  Publish immediately (visible to users)
                </label>
              </div>

              {/* Preview */}
              {form.title && (
                <div style={{ padding: '0.85rem 1rem', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', borderLeft: '3px solid #c9a84c' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.63rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Preview</div>
                  <div style={{ fontWeight: '600', fontSize: '0.85rem', marginBottom: '0.2rem', color: 'var(--text)' }}>📢 {form.title}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{form.message}</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
                <button onClick={() => setShowForm(false)} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer' }}>Cancel</button>
                <button onClick={saveMessage} disabled={saving} style={{ background: '#0d0d0d', color: '#f5f0e8', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer', textTransform: 'uppercase' }}>
                  {saving ? 'Publishing...' : '📢 Publish'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}